import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { auth, getCurrentUser, signIn, signUp, signInWithGoogle, logOut } from './firebase';
import { db } from './firebase';
import { doc, setDoc } from 'firebase/firestore';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<User>;
  signUpWithEmail: (email: string, password: string) => Promise<User>;
  signInWithGoogleAuth: () => Promise<User>;
  signOut: () => Promise<void>;
  allowedDomains: string[];
}

const ALLOWED_DOMAINS = ['thirdwishcreative.com', 'wonderhorse.com'];

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  signInWithEmail: () => Promise.reject(),
  signUpWithEmail: () => Promise.reject(),
  signInWithGoogleAuth: () => Promise.reject(),
  signOut: () => Promise.reject(),
  allowedDomains: ALLOWED_DOMAINS
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to save user data to Firestore
  const saveUserToFirestore = async (user: User) => {
    if (!user.uid || !user.email) return;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || null,
        lastLogin: new Date()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving user to Firestore:', error);
    }
  };

  useEffect(() => {
    // Add error handling to prevent app from crashing if Firebase auth is not initialized
    let unsubscribe = () => {};
    
    try {
      // Only set up the auth state listener if auth is properly initialized
      if (auth && typeof auth.onAuthStateChanged === 'function') {
        unsubscribe = auth.onAuthStateChanged((user) => {
          setCurrentUser(user);
          
          // If user is logged in, save their data to Firestore
          if (user) {
            saveUserToFirestore(user);
          }
          
          setLoading(false);
        }, (error) => {
          console.error("Auth state change error:", error);
          setLoading(false);
        });
      } else {
        console.warn("Firebase auth not properly initialized");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error setting up auth state listener:", error);
      setLoading(false);
    }

    return () => {
      try {
        unsubscribe();
      } catch (error) {
        console.error("Error unsubscribing from auth state:", error);
      }
    };
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    try {
      return await signIn(email, password);
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      return await signUp(email, password);
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  };

  const signInWithGoogleAuth = async () => {
    try {
      return await signInWithGoogle();
    } catch (error) {
      console.error("Google sign in error:", error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      return await logOut();
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogleAuth,
    signOut,
    allowedDomains: ALLOWED_DOMAINS
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 